import React, { useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";

import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { sendLeadQuote, getLeadQuoteLink, extendLeadQuoteLink, revokeLeadQuoteLink, getLeadSettings, type QuoteLink } from "api/Leads";

/**
 * "Send to lead" for one quote on the lead record (s.16.2 last bullet,
 * s.16.3 mechanics). Mints the public link, emails it from the owner's
 * mailbox, and shows the link's life afterwards: views, expiry, Extend,
 * Revoke, acceptance.
 */

const input: React.CSSProperties = { padding: "7px 9px", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: 6, fontSize: 13, fontFamily: "Poppins", width: "100%", boxSizing: "border-box" };

export default function SendQuotePanel({
  leadId,
  quoteId,
  quoteNumber,
  defaultTo,
  autoOpen = false,
  onChange,
}: {
  leadId: string;
  quoteId: string;
  quoteNumber: string;
  defaultTo: string;
  /** Open the send form straight away - arriving from "New quote". */
  autoOpen?: boolean;
  onChange?: () => void;
}) {
  const toast = useToast();
  const [link, setLink] = useState<QuoteLink | null | undefined>(undefined);
  const [open, setOpen] = useState(autoOpen);
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState(defaultTo);
  // Extra recipients, comma separated (several people at the lead).
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [message, setMessage] = useState("");
  const [requirePassword, setRequirePassword] = useState(true);
  const [includePassword, setIncludePassword] = useState(true);

  const load = () => getLeadQuoteLink(leadId, quoteId).then(setLink);
  useEffect(() => { load(); }, [leadId, quoteId]); // eslint-disable-line
  // Company default for "Require a password" (Lead settings).
  useEffect(() => { getLeadSettings().then((s) => setRequirePassword(!!s.quoteLinkPassword)); }, []);

  const act = async (fn: () => Promise<any>, ok: string) => {
    setBusy(true);
    const res = await fn();
    setBusy(false);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    toast.add(ok, "var(--theme-ui-colors-success)");
    setOpen(false);
    load();
    onChange?.();
  };

  const expired = link?.expiresAt ? new Date(link.expiresAt) < new Date() : false;

  return (
    <Box sx={{ fontSize: 12, fontFamily: "Poppins" }}>
      {link === undefined ? null : link ? (
        <Box>
          <Box sx={{ fontWeight: 600, color: link.acceptedAt ? "#27500A" : expired ? "#A60000" : themevals.colors.black }}>
            {link.acceptedAt
              ? `Accepted by ${link.acceptedName}${link.acceptedPoNumber ? ` \u00b7 PO ${link.acceptedPoNumber}` : ""}`
              : expired
                ? "Link expired"
                : `Link live \u00b7 ${link.viewCount} view${link.viewCount === 1 ? "" : "s"}`}
          </Box>
          <Box sx={{ color: themevals.colors.dkgrey, lineHeight: "18px" }}>
            {link.sentTo ? <Box>{`to ${link.sentTo}`}</Box> : null}
            <Box>
              {link.expiresAt ? `valid until ${new Date(link.expiresAt).toLocaleDateString()}` : ""}
              {link.password ? ` \u00b7 password ${link.password}` : " \u00b7 no password"}
            </Box>
          </Box>
          <Flex sx={{ columnGap: "14px", rowGap: "2px", mt: "6px", flexWrap: "wrap", justifyContent: "flex-start", alignItems: "center" }}>
            <button type="button" style={lnk} onClick={() => navigator.clipboard?.writeText(link.url)}>Copy link</button>
            {!link.acceptedAt && <button type="button" style={lnk} onClick={() => setOpen((v) => !v)}>{open ? "Cancel" : "Resend"}</button>}
            {!link.acceptedAt && <button type="button" style={lnk} disabled={busy} onClick={() => act(() => extendLeadQuoteLink(leadId, quoteId, 30), "Extended 30 days.")}>Extend 30 days</button>}
            {!link.acceptedAt && <button type="button" style={{ ...lnk, color: "#A60000" }} disabled={busy} onClick={() => act(() => revokeLeadQuoteLink(leadId, quoteId), "Link revoked.")}>Revoke</button>}
          </Flex>
        </Box>
      ) : (
        <button type="button" style={lnk} onClick={() => setOpen((v) => !v)}>{open ? "Cancel" : "Send to lead"}</button>
      )}

      {open && (
        <Box sx={{ mt: "8px", p: "12px", bg: "#FAFAF8", border: "1px solid", borderColor: "inputOutline", borderRadius: "8px", maxWidth: 420 }}>
          <input style={{ ...input, marginBottom: 6 }} placeholder="To (email)" value={to} onChange={(e) => setTo(e.target.value)} />
          <input style={{ ...input, marginBottom: 6 }} placeholder="CC (optional, separate with commas)" value={cc} onChange={(e) => setCc(e.target.value)} />
          <input style={{ ...input, marginBottom: 6 }} placeholder="BCC (optional, separate with commas)" value={bcc} onChange={(e) => setBcc(e.target.value)} />
          <textarea style={{ ...input, minHeight: 60, marginBottom: 6, fontFamily: "inherit" }} placeholder="A line or two above the button (optional)" value={message} onChange={(e) => setMessage(e.target.value)} />
          <label style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
            <input type="checkbox" checked={requirePassword} onChange={(e) => setRequirePassword(e.target.checked)} style={{ width: 14 }} /> Require a password
          </label>
          {requirePassword && (
            <label style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
              <input type="checkbox" checked={includePassword} onChange={(e) => setIncludePassword(e.target.checked)} style={{ width: 14 }} /> Include it in the email
            </label>
          )}
          <Flex sx={{ gap: 6 }}>
            <BasicButton type="button" disabled={busy || !to} $submitting={busy} onClick={() => act(() => sendLeadQuote(leadId, quoteId, { to, cc, bcc, message, requirePassword, includePassword }), `Quote ${quoteNumber} sent.`)}>
              Send
            </BasicButton>
            <BasicButton scheme="secondaryOutline" type="button" keepText disabled={busy} onClick={() => act(() => sendLeadQuote(leadId, quoteId, { to, requirePassword, skipEmail: true }), "Link created.")}>
              Link only
            </BasicButton>
          </Flex>
        </Box>
      )}
    </Box>
  );
}

// Inline on purpose: the global button CSS (min height, margins, full-width
// flex) spread these links across the cell and pushed Revoke to its own row.
const lnk: React.CSSProperties = { background: "none", border: "none", color: "#185FA5", fontFamily: "Poppins", fontSize: 12, fontWeight: 500, lineHeight: "18px", cursor: "pointer", padding: 0, margin: 0, width: "auto", height: "auto", minHeight: 0, minWidth: 0, flex: "0 0 auto", display: "inline", whiteSpace: "nowrap" };
